import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import LinkifiedText from "./LinkifiedText";
import FileViewerModal from "./FileViewerModal";
import MessageReactions from "./MessageReactions";
import { getViewerKind } from "../../utils/linkify";
import { Button, EmptyState } from "../../components/ui";
import { MessageSquare, Trash2, Info } from "lucide-react";

// Skeleton Component for initial loading & fetching older messages
const MessageSkeleton = ({ count = 4 }) => {
  return (
    <div className="space-y-4 py-2 animate-pulse w-full" aria-busy="true" aria-label="Loading messages">
      {Array.from({ length: count }).map((_, idx) => {
        const isRight = idx % 2 === 1;
        return (
          <div
            key={`skeleton-${idx}`}
            className={`w-full flex ${isRight ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-xs sm:max-w-md w-2/3 rounded-2xl p-3.5 space-y-2 ${
                isRight
                  ? "bg-neutral-300/60 rounded-br-none"
                  : "bg-neutral-200/60 rounded-bl-none"
              }`}
            >
              <div className="h-3.5 bg-neutral-300/80 rounded-md w-3/4"></div>
              <div className="h-3 bg-neutral-300/80 rounded-md w-1/2"></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MessageList = ({
  messages = [],
  currentUserId,
  onDeleteMessage,
  onOpenGroupInfo,
  onToggleReaction,
  activeConversation,
  typingUsers = [],
  loading = false, // Initial fetch loading flag from ChatPage
  isLoadingOlder = false, // Previous page loading flag
  onLoadOlderMessages,
}) => {
  const scrollRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const [viewer, setViewer] = useState({
    isOpen: false,
    file: null,
    kind: "OTHER",
  });

  const messageList = Array.isArray(messages)
    ? messages
    : messages?.data && Array.isArray(messages.data)
    ? messages.data
    : [];

  // Maintain position during pagination
  useLayoutEffect(() => {
    if (scrollRef.current && prevScrollHeightRef.current > 0) {
      const heightDifference =
        scrollRef.current.scrollHeight - prevScrollHeightRef.current;
      scrollRef.current.scrollTop += heightDifference;
      prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    }
  }, [messageList.length]);

  // Initial auto-scroll to bottom on fresh load
  useEffect(() => {
    if (scrollRef.current && !loading && !isLoadingOlder) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messageList, typingUsers, loading, isLoadingOlder]);

  const handleScroll = () => {
    if (!scrollRef.current || isLoadingOlder || loading || !onLoadOlderMessages) return;

    if (scrollRef.current.scrollTop === 0) {
      prevScrollHeightRef.current = scrollRef.current.scrollHeight;
      onLoadOlderMessages();
    }
  };

  const getSenderName = (msg) => {
    if (typeof msg.senderId === "object" && msg.senderId?.name) {
      return msg.senderId.name;
    }
    return "User";
  };

  const normalizeId = (value) => {
    if (value == null) return "";
    if (typeof value === "object") {
      if (
        typeof value.toString === "function" &&
        value._id == null &&
        value.id == null
      ) {
        const s = value.toString();
        if (s && s !== "[object Object]") return s;
      }
      return (value._id ?? value.id ?? "").toString();
    }
    return String(value);
  };

  const isOwnMessage = (msg) => {
    const me = normalizeId(currentUserId);
    if (!me) return false;
    const candidates = [
      msg.rawSenderId,
      msg.senderId,
      msg.senderId?._id,
      msg.senderId?.id,
    ];
    return candidates.some((c) => {
      const s = normalizeId(c);
      return s && s === me;
    });
  };

  const openViewer = (file, messageType) => {
    if (!file?.url) return;
    setViewer({
      isOpen: true,
      file,
      kind: getViewerKind(file, messageType),
    });
  };

  const closeViewer = () =>
    setViewer({ isOpen: false, file: null, kind: "OTHER" });

  const renderAttachment = (msg, own) => {
    if (!msg.file?.url) return null;
    const kind = getViewerKind(msg.file, msg.type);
    const chipClass = own
      ? "bg-neutral-800 text-white hover:bg-neutral-900"
      : "bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 hover:bg-neutral-200 dark:hover:bg-neutral-700";

    if (kind === "IMAGE") {
      return (
        <div className="mb-2">
          <button
            type="button"
            onClick={() => openViewer(msg.file, msg.type)}
            className="block w-full text-left"
            title="View image"
          >
            <img
              src={msg.file.url}
              alt={msg.file.originalName || "Attachment"}
              className="max-w-full rounded-xl max-h-60 object-cover cursor-pointer hover:opacity-95 border border-black/10 shadow-xs"
            />
          </button>
          <div className="flex gap-2 mt-1.5">
            <Button
              type="button"
              variant={own ? "ghost" : "outline"}
              size="xs"
              onClick={() => openViewer(msg.file, msg.type)}
              className={`text-[10px] px-2 py-0.5 h-auto ${
                own ? "text-white hover:bg-neutral-800" : ""
              }`}
            >
              View
            </Button>
            <Button
              type="button"
              variant={own ? "ghost" : "outline"}
              size="xs"
              tag="a"
              href={msg.file.url}
              download={msg.file.originalName || "image"}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-[10px] px-2 py-0.5 h-auto ${
                own ? "text-white hover:bg-neutral-800" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              Download
            </Button>
          </div>
        </div>
      );
    }

    const icon =
      kind === "PDF"
        ? "📄"
        : kind === "EXCEL"
        ? "📊"
        : kind === "WORD"
        ? "📝"
        : "📎";

    return (
      <div className="mb-2">
        <button
          type="button"
          onClick={() => openViewer(msg.file, msg.type)}
          className={`w-full flex items-center gap-2 p-2.5 rounded-xl text-xs text-left transition ${chipClass}`}
        >
          <span className="text-base">{icon}</span>
          <span className="truncate flex-1 font-medium">
            {msg.file.originalName || "Attachment"}
          </span>
          <span className="shrink-0 opacity-80 text-[11px]">View</span>
        </button>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-neutral-50/50 min-h-0">
      {/* Header */}
      <div className="p-4 bg-white dark:bg-neutral-900 border-b border-neutral-200/60 flex justify-between items-center shadow-xs shrink-0">
        <div>
          <h2 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            {activeConversation?.type === "GROUP"
              ? activeConversation.name || "Group Chat"
              : "Direct Conversation"}
          </h2>
          {activeConversation?.type === "GROUP" && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {activeConversation.participants?.length || 0} members
            </p>
          )}
          {typingUsers.length > 0 && (
            <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-0.5 animate-pulse font-medium">
              {typingUsers.length === 1
                ? "Someone is typing..."
                : `${typingUsers.length} people are typing...`}
            </p>
          )}
        </div>
        {activeConversation?.type === "GROUP" && (
          <Button
            variant="outline"
            size="xs"
            onClick={onOpenGroupInfo}
            leftIcon={<Info className="w-3.5 h-3.5" />}
            className="text-xs font-semibold py-1.5"
          >
            Group Info
          </Button>
        )}
      </div>

      {/* Message Feed */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-3"
      >
        {/* Render Skeleton if initial loading OR fetching older messages */}
        {loading ? (
          <MessageSkeleton count={5} />
        ) : (
          <>
            {isLoadingOlder && <MessageSkeleton count={2} />}

            {messageList.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <EmptyState
                  icon={MessageSquare}
                  title="No messages here yet"
                  description="Start the conversation by sending a message below."
                />
              </div>
            ) : (
              messageList.map((msg) => {
                const own = isOwnMessage(msg);
                const senderName = getSenderName(msg);

                return (
                  <div
                    key={msg._id || Math.random()}
                    className={`w-full flex ${
                      own ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-xs sm:max-w-md rounded-2xl px-4 py-2.5 shadow-xs relative group ${ own ? "bg-neutral-900 text-white rounded-br-none" : "bg-white dark:bg-neutral-900 border border-neutral-200/80 text-neutral-900 dark:text-neutral-100 rounded-bl-none" }`}
                    >
                      {!own && activeConversation?.type === "GROUP" && (
                        <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1">
                          {senderName}
                        </div>
                      )}

                      {renderAttachment(msg, own)}

                      {msg.content && (
                        <LinkifiedText text={msg.content} own={own} />
                      )}

                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        <span className="text-[10px] text-neutral-400 dark:text-neutral-500">
                          {msg.createdAt
                            ? new Date(msg.createdAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>

                        {own && onDeleteMessage && (
                          <button
                            type="button"
                            onClick={() => onDeleteMessage(msg._id)}
                            className="opacity-0 group-hover:opacity-100 text-rose-300 hover:text-rose-100 transition p-0.5"
                            title="Delete Message"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>

                      {onToggleReaction && (
                        <MessageReactions
                          reactions={msg.reactions || []}
                          currentUserId={currentUserId}
                          own={own}
                          onToggleReaction={(emoji) =>
                            onToggleReaction(msg._id, emoji)
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      <FileViewerModal
        isOpen={viewer.isOpen}
        onClose={closeViewer}
        file={viewer.file || {}}
        kind={viewer.kind}
      />
    </div>
  );
};

export default MessageList;